import { forwardRef, type Ref } from "react";

import {
  CONSOLE_FILTER_STRIP_NOTES,
  CONSOLE_UNAVAILABLE_ACTION_LABELS,
  CONSOLE_WINDOW_SCANS,
  REPORTS_RETRIEVAL_MAILTO,
  REPORTS_WINDOW_ABOUT_ROWS,
  consoleFilterChipText,
  consoleUnavailableCopy,
  consoleWindowLineCopy,
  type ConsoleFilterChip,
  type ConsoleFilterChipLabel,
  type ConsoleFilterFooterState,
  type ConsoleFilterRefusal,
  type ConsoleFilterStripNote,
  type ConsoleFilterSurface,
  type ConsoleUnavailableAction,
  type ConsoleWindowAboutVariant,
  type ConsoleWindowVariant,
} from "../../data/console-filter-contract";

export interface FilterStripProps {
  readonly chips: readonly ConsoleFilterChip[];
  readonly onClearAll: () => void;
  readonly note?: ConsoleFilterStripNote;
  readonly onRemoveChip?: (label: ConsoleFilterChipLabel) => void;
  /** Optional refs so Reports can restore keyboard focus after a chip unmounts. */
  readonly chipRemoveRefs?: Partial<
    Record<ConsoleFilterChipLabel, Ref<HTMLButtonElement>>
  >;
}

/** Active filter chip strip: Label · Value, Clear all, optional closed note (D-01/D-03). */
export function FilterStrip({
  chips,
  onClearAll,
  note,
  onRemoveChip,
  chipRemoveRefs,
}: FilterStripProps) {
  return (
    <div className="report__filter-strip" role="group" aria-label="Active filters">
      <span className="report__filter-strip-label">Filters</span>
      {chips.map((chip) => {
        const datesClass =
          chip.label === "Dates" ? " report__filter-chip--dates" : "";
        return (
          <span
            className={`report__filter-chip${datesClass}`}
            key={`${chip.label}:${chip.value}`}
          >
            {consoleFilterChipText(chip.label, chip.value)}
            {chip.removable ? (
              <button
                ref={chipRemoveRefs?.[chip.label]}
                type="button"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => onRemoveChip?.(chip.label)}
              >
                ×
              </button>
            ) : null}
          </span>
        );
      })}
      <button className="report__clear" type="button" onClick={onClearAll}>
        Clear all
      </button>
      {note !== undefined ? (
        <p className="report__note">{CONSOLE_FILTER_STRIP_NOTES[note]}</p>
      ) : null}
    </div>
  );
}

export interface WindowLineProps {
  readonly variant: ConsoleWindowVariant;
  readonly about?: ConsoleWindowAboutVariant;
}

/** Quiet window honesty line under the strip (D-04). */
export function WindowLine({ variant, about }: WindowLineProps) {
  return (
    <div className="report__window-note">
      <span>{consoleWindowLineCopy(variant)}</span>
      {about === "reports_scans" ? <ReportsWindowAbout /> : null}
    </div>
  );
}

function ReportsWindowAbout() {
  return (
    <details className="report__about">
      <summary>About this window</summary>
      <dl>
        {REPORTS_WINDOW_ABOUT_ROWS.map((row) => (
          <div className="report__about-row" key={row.term}>
            <dt>{row.term}</dt>
            <dd>
              {row.href !== undefined ? (
                <a href={row.href}>{row.linkLabel}</a>
              ) : (
                row.detail
              )}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export interface ResultFooterProps {
  readonly state: ConsoleFilterFooterState;
  /** Precomputed closed-copy foot sentence (Reports compatibility uses reportsResultCopy). */
  readonly foot: string;
}

/** Result honesty footer: all / capped / none (D-05). Cap stays neutral. */
export function ResultFooter({ state, foot }: ResultFooterProps) {
  return (
    <p
      className={`report__foot${state === "capped" ? " report__foot--capped" : ""}`}
    >
      {foot}
    </p>
  );
}

export interface UnavailableNoticeProps {
  readonly surface: ConsoleFilterSurface;
  readonly reason: ConsoleFilterRefusal;
  readonly actions?: readonly ConsoleUnavailableAction[];
  readonly onAction?: (action: ConsoleUnavailableAction) => void;
  readonly kicker?: "unavailable_not_empty";
  /** Replay outside-retention days (runtime policy). */
  readonly retentionDays?: number;
  /** Replay outside vs lapsed context (FG-07). */
  readonly replayContext?: "outside" | "lapsed";
}

/**
 * Dashed unavailable pane with closed reason copy (D-06 / D-07).
 * Never renders a table.
 */
export const UnavailableNotice = forwardRef<HTMLDivElement, UnavailableNoticeProps>(
  function UnavailableNotice(
    { surface, reason, actions = [], onAction, kicker, retentionDays, replayContext },
    ref,
  ) {
    return (
      <div className="report__unavailable" role="status" ref={ref}>
        <div>
          {kicker === "unavailable_not_empty" ? (
            <p className="report__state-kicker">Unavailable, not empty</p>
          ) : null}
          <p>
            {consoleUnavailableCopy({
              surface,
              reason,
              ...(retentionDays !== undefined ? { retentionDays } : {}),
              ...(replayContext !== undefined ? { replayContext } : {}),
            })}
          </p>
          {actions.length > 0 ? (
            <div className="report__state-actions">
              {actions.map((action) =>
                action === "request_retrieval" ? (
                  <a key={action} href={REPORTS_RETRIEVAL_MAILTO}>
                    {CONSOLE_UNAVAILABLE_ACTION_LABELS[action]}
                  </a>
                ) : (
                  <button
                    key={action}
                    type="button"
                    onClick={() => onAction?.(action)}
                  >
                    {CONSOLE_UNAVAILABLE_ACTION_LABELS[action]}
                  </button>
                ),
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

/** Convenience re-export so Reports can pin the scans window constant next to WindowLine. */
export { CONSOLE_WINDOW_SCANS };
